import {
  Button,
  DatePicker,
  Dropdown,
  formatNumber,
  getDateWithHumanReadableTime,
  ItemSplitter,
  OverflowContainer,
  RowFlex,
  sortAndFormatOptions,
  Table,
  TextField,
  theme,
  Toggle,
  useToast,
} from "common";
import { orderBy } from "lodash";
import { useState } from "react";
import { isSameMonth } from "date-fns";
import { eBrandGoalType, isBrandOpen } from "../static";

const AddGoals = ({
  brands: brandsToUse = [],
  thisDate,
  close,
  doMany,
  onCompleted,
  loading,
}) => {
  const { alertSuccess, onError } = useToast();
  const [search, setSearch] = useState("");
  const [sosThisMonth, setSosThisMonth] = useState(false);
  const [brands, setBrands] = useState(
    orderBy(brandsToUse, "name", "asc").map((b) => {
      const { isOpen, openPos } = isBrandOpen(b);
      const posByDate = orderBy(
        b.purchaseOrders,
        (p) => new Date(p.PODate),
        "desc"
      );
      const latestPo = posByDate[0];
      const latestDate = getDateWithHumanReadableTime(latestPo?.PODate);
      const latestAmount = formatNumber(latestPo?.Amount);
      const latest = latestPo ? ` (${latestDate} - $${latestAmount})` : "";
      const display = `${b.name}${latest}`;
      const sosThisMonth =
        b.saleOrders?.filter((so) =>
          isSameMonth(new Date(so.OrderDate), thisDate)
        ) ?? [];
      return {
        isOpen,
        isNew: false,
        openPos,
        type: isOpen ? eBrandGoalType.OPEN : eBrandGoalType.CLOSED,
        display,
        saleOrders: sortAndFormatOptions({
          list: b.saleOrders ?? [],
          valueKey: "id",
          labelKey: (v) => `${v.OrderNumber} ($${formatNumber(v.Amount)})`,
        }),
        sosThisMonth,
        hasSoThisMonth: sosThisMonth.length > 0,
        brand: b.id,
        goalAmount: 0,
        notes: "",
        repeat: false,
        month: thisDate,
        customSos: [],
      };
    })
  );
  const updateBrand = (id, key, value) =>
    setBrands((p) =>
      p.map((bb) => ({ ...bb, [key]: bb.brand === id ? value : bb[key] }))
    );

  let good = 0;

  return (
    <OverflowContainer
      titleWithBodyAndActions
      style={{ width: "90vw", height: "75vh" }}
    >
      <RowFlex extend>
        <h3>Add Goals</h3>
        <RowFlex>
          <div style={{ width: "200px" }}>
            <TextField
              placeholder="Search Brand"
              value={search}
              onChange={(v) => setSearch(v)}
            />
          </div>
          <Toggle
            checked={sosThisMonth}
            onChange={() => setSosThisMonth((p) => !p)}
            label="Current SOs Only"
          />
        </RowFlex>
      </RowFlex>
      <OverflowContainer>
        <Table>
          <thead>
            <tr>
              <th style={{ width: "40%" }}>Brand</th>
              <th style={{ width: "5%" }}>Open</th>
              <th style={{ width: "1%" }}>Month</th>
              <th style={{ width: "20%" }}>Goal *</th>
              <th>Custom SOs</th>
              <th>Notes</th>
              <th>New</th>
              <th style={{ width: "1%" }}>Repeat</th>
            </tr>
          </thead>
          <tbody>
            {brands
              .filter((b) => {
                const searchFilter =
                  !search ||
                  b.display.toLowerCase().includes(search.toLowerCase());
                const thisMonthFilter = !sosThisMonth || !!b.hasSoThisMonth;
                return searchFilter && thisMonthFilter;
              })
              .map((b) => {
                const isGood = b.goalAmount > 0;
                good += isGood ? 1 : 0;
                return (
                  <tr key={b.id}>
                    <td>
                      <RowFlex>
                        <div
                          style={{
                            width: "1rem",
                            height: "1rem",
                            borderRadius: "100px",
                            background: isGood && theme.colors.success,
                          }}
                        ></div>
                        {b.display}
                      </RowFlex>
                    </td>
                    <td>
                      {b.isOpen && (
                        <RowFlex center style={{ fontSize: "0.7rem" }}>
                          <div>
                            {b.openPos.map((po) => (
                              <div key={po.PONumber}>
                                {po.PONumber} - ${po.Amount} -{" "}
                                {getDateWithHumanReadableTime(po.PODate)}
                              </div>
                            ))}
                          </div>
                        </RowFlex>
                      )}
                    </td>
                    <td>
                      <DatePicker
                        value={b.month}
                        onChange={(v) => updateBrand(b.brand, "month", v)}
                      />
                    </td>
                    <td>
                      <TextField
                        value={b.goalAmount}
                        type="number"
                        onChange={(v) => updateBrand(b.brand, "goalAmount", v)}
                      />
                    </td>
                    <td>
                      <Dropdown
                        isMulti
                        options={b.saleOrders}
                        value={b.customSos}
                        onChange={(v) => updateBrand(b.brand, "customSos", v)}
                      />
                    </td>
                    <td>
                      <TextField
                        value={b.notes}
                        onChange={(v) => updateBrand(b.brand, "notes", v)}
                      />
                    </td>
                    <td>
                      <Toggle
                        checked={b.isNew}
                        onChange={() => updateBrand(b.brand, "isNew", !b.isNew)}
                      />
                    </td>
                    <td>
                      <Toggle
                        checked={b.repeat}
                        onChange={() =>
                          updateBrand(b.brand, "repeat", !b.repeat)
                        }
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      </OverflowContainer>
      <ItemSplitter>
        <Button secondary onClick={close}>
          Cancel
        </Button>
        <Button
          disabled={good === 0 || loading}
          onClick={() => {
            const payloads = brands
              .filter((b) => b.goalAmount > 0)
              .map((b) => ({
                repeat: b.repeat,
                brand: b.brand,
                goalAmount: b.goalAmount,
                notes: b.notes,
                customSos: b.customSos,
                month: b.month,
                type: b.isNew
                  ? eBrandGoalType.NEW
                  : b.isOpen
                  ? eBrandGoalType.OPEN
                  : eBrandGoalType.CLOSED,
              }));
            doMany(payloads)
              .then(() => {
                alertSuccess(`${good} Goals Added!`);
                onCompleted();
              })
              .catch(onError);
          }}
        >
          Add {good} Goals
        </Button>
      </ItemSplitter>
    </OverflowContainer>
  );
};

export default AddGoals;
